import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  X,
  FileText,
  RefreshCw,
  Download,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { EmptyState } from '../ui/EmptyState';
import { Button } from '../Button';
import type { FormResponse, Form } from '../../types/forms';

interface ResponseWithForm extends FormResponse {
  form?: Form;
}

type SortField = 'created_at' | 'status' | 'form_title';
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Função segura para extrair texto de busca dos dados da resposta
// Evita JSON.stringify que causa crash com objetos grandes
const extractSearchableText = (data: Record<string, unknown> | null | undefined): string => {
  if (!data || typeof data !== 'object') return '';

  try {
    const values: string[] = [];
    const extractValues = (obj: unknown, depth = 0): void => {
      // Limitar profundidade para evitar loops infinitos
      if (depth > 3) return;

      if (obj === null || obj === undefined) return;

      if (typeof obj === 'string') {
        values.push(obj.toLowerCase());
      } else if (typeof obj === 'number' || typeof obj === 'boolean') {
        values.push(String(obj).toLowerCase());
      } else if (Array.isArray(obj)) {
        obj.slice(0, 10).forEach(item => extractValues(item, depth + 1)); // Limitar a 10 itens
      } else if (typeof obj === 'object') {
        Object.values(obj).slice(0, 20).forEach(val => extractValues(val, depth + 1)); // Limitar a 20 propriedades
      }
    };

    extractValues(data);
    return values.join(' ');
  } catch {
    return '';
  }
};

export const FormsResponsesAll: React.FC = () => {
  const navigate = useNavigate();
  const [responses, setResponses] = useState<ResponseWithForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'new' | 'in_progress' | 'completed'>('all');
  const [filterFormId, setFilterFormId] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Sorting
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Forms for filter dropdown
  const [forms, setForms] = useState<Form[]>([]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const fetchData = async () => {
      try {
        setError(null);

        // Timeout de segurança - se demorar mais de 10s, para o loading
        timeoutId = setTimeout(() => {
          if (isMounted && loading) {
            console.warn('Fetch timeout - stopping loading state');
            setLoading(false);
            setError('A busca demorou muito. Tente atualizar a página.');
          }
        }, 10000);

        // Fetch user's forms first
        const { data: formsData, error: formsError } = await supabase
          .from('forms')
          .select('*')
          .order('title', { ascending: true });

        if (!isMounted) return;

        if (formsError) {
          console.error('Error fetching forms:', formsError);
          setError('Erro ao carregar formulários');
          setLoading(false);
          return;
        }

        const safeFormsData = formsData || [];
        const formsMap = new Map<string, Form>();
        safeFormsData.forEach((f: Form) => formsMap.set(f.id, f));
        setForms(safeFormsData);

        const formIds = safeFormsData.map((f: Form) => f.id);

        // Se não há formulários, não há respostas - termina aqui
        if (formIds.length === 0) {
          setResponses([]);
          setLoading(false);
          return;
        }

        // Fetch responses for user's forms
        const { data: responsesData, error: responsesError } = await supabase
          .from('form_responses')
          .select('*')
          .in('form_id', formIds)
          .order('created_at', { ascending: false });

        if (!isMounted) return;

        if (responsesError) {
          console.error('Error fetching responses:', responsesError);
          // Mesmo com erro, mostra tela vazia ao invés de loading infinito
          setResponses([]);
        } else {
          // Manually attach form data to each response
          const responsesWithForm = (responsesData || []).map((r: FormResponse) => ({
            ...r,
            form: formsMap.get(r.form_id),
          }));
          setResponses(responsesWithForm);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        if (isMounted) {
          setResponses([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
        clearTimeout(timeoutId);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);

    try {
      const { data: formsData, error: formsError } = await supabase
        .from('forms')
        .select('*');

      if (formsError) {
        setError('Erro ao atualizar');
        return;
      }

      const safeFormsData = formsData || [];
      const formsMap = new Map<string, Form>();
      safeFormsData.forEach((f: Form) => formsMap.set(f.id, f));
      setForms(safeFormsData);

      const formIds = safeFormsData.map((f: Form) => f.id);

      if (formIds.length > 0) {
        const { data, error: responsesError } = await supabase
          .from('form_responses')
          .select('*')
          .in('form_id', formIds)
          .order('created_at', { ascending: false });

        if (responsesError) {
          setError('Erro ao atualizar respostas');
        } else {
          const responsesWithForm = (data || []).map((r: FormResponse) => ({
            ...r,
            form: formsMap.get(r.form_id),
          }));
          setResponses(responsesWithForm);
        }
      } else {
        setResponses([]);
      }
    } catch (err) {
      console.error('Error refreshing:', err);
      setError('Erro ao atualizar');
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Stats memoizados - só recalcula quando responses muda
  const stats = useMemo(() => {
    return {
      total: responses.length,
      new: responses.filter(r => r.status === 'new').length,
      inProgress: responses.filter(r => r.status === 'in_progress').length,
      completed: responses.filter(r => r.status === 'completed').length,
    };
  }, [responses]);

  // Filter and sort responses - com busca segura
  const processedResponses = useMemo(() => {
    try {
      let result = [...responses];

      // Text search - usando função segura
      if (searchTerm) {
        const search = searchTerm.toLowerCase().trim();
        if (search) {
          result = result.filter(response => {
            const formTitle = (response.form?.title || '').toLowerCase();
            if (formTitle.includes(search)) return true;

            // Busca segura nos dados
            const searchableText = extractSearchableText(response.data);
            return searchableText.includes(search);
          });
        }
      }

      // Status filter
      if (filterStatus !== 'all') {
        result = result.filter(r => r.status === filterStatus);
      }

      // Form filter
      if (filterFormId !== 'all') {
        result = result.filter(r => r.form_id === filterFormId);
      }

      // Date range filter
      if (dateFrom) {
        try {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          result = result.filter(r => {
            try {
              return new Date(r.created_at) >= fromDate;
            } catch {
              return true;
            }
          });
        } catch {
          // Ignorar erro de data inválida
        }
      }

      if (dateTo) {
        try {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          result = result.filter(r => {
            try {
              return new Date(r.created_at) <= toDate;
            } catch {
              return true;
            }
          });
        } catch {
          // Ignorar erro de data inválida
        }
      }

      // Sort
      result.sort((a, b) => {
        let comparison = 0;
        try {
          switch (sortField) {
            case 'created_at':
              comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
              break;
            case 'status':
              comparison = (a.status || '').localeCompare(b.status || '');
              break;
            case 'form_title':
              comparison = (a.form?.title || '').localeCompare(b.form?.title || '');
              break;
          }
        } catch {
          comparison = 0;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });

      return result;
    } catch (err) {
      console.error('Error processing responses:', err);
      return responses;
    }
  }, [responses, searchTerm, filterStatus, filterFormId, dateFrom, dateTo, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(processedResponses.length / itemsPerPage));

  const paginatedResponses = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return processedResponses.slice(start, start + itemsPerPage);
  }, [processedResponses, currentPage, itemsPerPage]);

  // Reset page when filters change - separado de itemsPerPage para evitar loop
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterFormId, dateFrom, dateTo]);

  // Ajustar página quando itemsPerPage muda (separado)
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(processedResponses.length / itemsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [itemsPerPage, processedResponses.length, currentPage]);

  const hasActiveFilters = searchTerm || filterStatus !== 'all' || filterFormId !== 'all' || dateFrom || dateTo;

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterFormId('all');
    setDateFrom('');
    setDateTo('');
  }, []);

  const toggleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);

  const getStatusConfig = useCallback((status: string) => {
    switch (status) {
      case 'new':
        return { label: 'Nova', color: 'text-blue-400 bg-blue-500/20', icon: AlertCircle };
      case 'in_progress':
        return { label: 'Em andamento', color: 'text-yellow-400 bg-yellow-500/20', icon: Clock };
      case 'completed':
        return { label: 'Concluída', color: 'text-green-400 bg-green-500/20', icon: CheckCircle };
      default:
        return { label: status || 'Desconhecido', color: 'text-neutral-400 bg-neutral-500/20', icon: AlertCircle };
    }
  }, []);

  const formatDate = useCallback((dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Data inválida';
    }
  }, []);

  const formatDateShort = useCallback((dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    } catch {
      return 'Data inválida';
    }
  }, []);

  const exportToCSV = useCallback(() => {
    try {
      const headers = ['Formulário', 'Status', 'Data', 'Dados'];
      const rows = processedResponses.map(r => [
        r.form?.title || '',
        r.status || '',
        formatDate(r.created_at),
        JSON.stringify(r.data || {}),
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `respostas_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  }, [processedResponses, formatDate]);

  // Gerar números de página de forma eficiente
  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [1];

    if (currentPage > 3) {
      pages.push('ellipsis');
    }

    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    if (currentPage < totalPages - 2) {
      pages.push('ellipsis');
    }

    if (!pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return pages;
  }, [totalPages, currentPage]);

  // Render
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Todas as Respostas</h1>
          <p className="text-neutral-400 mt-1">
            {loading ? 'Carregando...' : `${processedResponses.length} ${processedResponses.length === 1 ? 'resposta' : 'respostas'}${hasActiveFilters ? ' (filtradas)' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="p-2.5 rounded-xl bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <Button
            variant="outline"
            onClick={exportToCSV}
            disabled={processedResponses.length === 0 || loading}
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <button
          onClick={() => setFilterStatus('all')}
          className={`liquid-glass rounded-xl p-4 text-left transition-all ${filterStatus === 'all' ? 'ring-2 ring-primary-500' : ''}`}
        >
          <p className="text-2xl font-bold text-white">{loading ? '-' : stats.total}</p>
          <p className="text-sm text-neutral-400">Total</p>
        </button>
        <button
          onClick={() => setFilterStatus('new')}
          className={`liquid-glass rounded-xl p-4 text-left transition-all ${filterStatus === 'new' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <p className="text-2xl font-bold text-blue-400">{loading ? '-' : stats.new}</p>
          <p className="text-sm text-neutral-400">Novas</p>
        </button>
        <button
          onClick={() => setFilterStatus('in_progress')}
          className={`liquid-glass rounded-xl p-4 text-left transition-all ${filterStatus === 'in_progress' ? 'ring-2 ring-yellow-500' : ''}`}
        >
          <p className="text-2xl font-bold text-yellow-400">{loading ? '-' : stats.inProgress}</p>
          <p className="text-sm text-neutral-400">Em andamento</p>
        </button>
        <button
          onClick={() => setFilterStatus('completed')}
          className={`liquid-glass rounded-xl p-4 text-left transition-all ${filterStatus === 'completed' ? 'ring-2 ring-green-500' : ''}`}
        >
          <p className="text-2xl font-bold text-green-400">{loading ? '-' : stats.completed}</p>
          <p className="text-sm text-neutral-400">Concluídas</p>
        </button>
      </div>

      {/* Filters */}
      <div className="liquid-glass rounded-2xl p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
            <input
              type="text"
              placeholder="Buscar respostas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-neutral-900/50 border border-neutral-700 rounded-xl pl-12 pr-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            />
          </div>

          {/* Form Filter */}
          <div className="relative">
            <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
            <select
              value={filterFormId}
              onChange={(e) => setFilterFormId(e.target.value)}
              className="w-full appearance-none bg-neutral-900/50 border border-neutral-700 rounded-xl pl-12 pr-10 py-3 text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 cursor-pointer"
            >
              <option value="all">Todos os formulários</option>
              {forms.map(form => (
                <option key={form.id} value={form.id}>{form.title}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div className="relative">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full bg-neutral-900/50 border border-neutral-700 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 [color-scheme:dark]"
              placeholder="Data inicial"
            />
            {!dateFrom && (
              <span className="absolute left-12 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none">
                De
              </span>
            )}
          </div>

          {/* Date To */}
          <div className="relative">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full bg-neutral-900/50 border border-neutral-700 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 [color-scheme:dark]"
              placeholder="Data final"
            />
            {!dateTo && (
              <span className="absolute left-12 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none">
                Até
              </span>
            )}
          </div>
        </div>

        {/* Active Filters & Clear */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-neutral-700/50">
            <span className="text-sm text-neutral-500">Filtros ativos:</span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-primary-500/20 text-primary-400">
                  Busca: "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filterStatus !== 'all' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-primary-500/20 text-primary-400">
                  Status: {getStatusConfig(filterStatus).label}
                  <button onClick={() => setFilterStatus('all')} className="hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filterFormId !== 'all' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-primary-500/20 text-primary-400">
                  Formulário: {forms.find(f => f.id === filterFormId)?.title || 'Selecionado'}
                  <button onClick={() => setFilterFormId('all')} className="hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {dateFrom && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-primary-500/20 text-primary-400">
                  De: {formatDateShort(dateFrom)}
                  <button onClick={() => setDateFrom('')} className="hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {dateTo && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-primary-500/20 text-primary-400">
                  Até: {formatDateShort(dateTo)}
                  <button onClick={() => setDateTo('')} className="hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
            <button
              onClick={clearFilters}
              className="text-sm text-neutral-400 hover:text-white ml-auto"
            >
              Limpar todos
            </button>
          </div>
        )}
      </div>

      {/* Sort Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-neutral-500">Ordenar por:</span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => toggleSort('created_at')}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                sortField === 'created_at'
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'bg-neutral-800/50 text-neutral-400 hover:text-white'
              }`}
            >
              Data
              {sortField === 'created_at' && (
                <ArrowUpDown className={`w-3 h-3 ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
              )}
            </button>
            <button
              onClick={() => toggleSort('status')}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                sortField === 'status'
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'bg-neutral-800/50 text-neutral-400 hover:text-white'
              }`}
            >
              Status
              {sortField === 'status' && (
                <ArrowUpDown className={`w-3 h-3 ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
              )}
            </button>
            <button
              onClick={() => toggleSort('form_title')}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                sortField === 'form_title'
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'bg-neutral-800/50 text-neutral-400 hover:text-white'
              }`}
            >
              Formulário
              {sortField === 'form_title' && (
                <ArrowUpDown className={`w-3 h-3 ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
              )}
            </button>
          </div>
        </div>

        {/* Items per page */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-500">Exibir:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="appearance-none bg-neutral-800/50 border border-neutral-700 rounded-lg px-3 py-1.5 text-base sm:text-sm text-white focus:outline-none focus:border-primary-500 cursor-pointer"
          >
            {ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Responses List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      ) : paginatedResponses.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="Nenhuma resposta encontrada"
          description={hasActiveFilters
            ? 'Tente ajustar os filtros para encontrar o que procura'
            : 'As respostas dos seus formulários aparecerão aqui'
          }
          action={hasActiveFilters ? (
            <Button variant="outline" onClick={clearFilters}>
              Limpar filtros
            </Button>
          ) : undefined}
        />
      ) : (
        <>
          <div className="space-y-3">
            {paginatedResponses.map((response) => {
              const statusConfig = getStatusConfig(response.status);
              const StatusIcon = statusConfig.icon;

              return (
                <div
                  key={response.id}
                  onClick={() => navigate(`/dashboard/forms/${response.form_id}/responses`)}
                  className="liquid-glass rounded-2xl p-5 hover:border-neutral-600 transition-all cursor-pointer group"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Status Badge */}
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${statusConfig.color}`}>
                        <StatusIcon className="w-5 h-5" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate group-hover:text-primary-400 transition-colors">
                          {response.form?.title || 'Formulário'}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                          <span className="text-sm text-neutral-500">
                            {formatDate(response.created_at)}
                          </span>
                          {response.internal_notes && (
                            <span className="text-xs text-neutral-600">
                              Com notas internas
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action */}
                    <ExternalLink className="w-5 h-5 text-neutral-500 group-hover:text-primary-400 transition-colors flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-neutral-800">
              <p className="text-sm text-neutral-500">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, processedResponses.length)} de {processedResponses.length} respostas
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-neutral-800/50 text-neutral-400 hover:text-white hover:bg-neutral-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <ChevronLeft className="w-4 h-4 -ml-2" />
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-neutral-800/50 text-neutral-400 hover:text-white hover:bg-neutral-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-1">
                  {pageNumbers.map((page, index) => (
                    page === 'ellipsis' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-neutral-600">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary-500 text-white'
                            : 'bg-neutral-800/50 text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-neutral-800/50 text-neutral-400 hover:text-white hover:bg-neutral-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-neutral-800/50 text-neutral-400 hover:text-white hover:bg-neutral-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4 -ml-2" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
